import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ShieldCheck, 
  Globe, 
  Clock, 
  HeartHandshake, 
  TrendingUp, 
  BadgeCheck 
} from "lucide-react";

const reasons = [
  {
    icon: ShieldCheck,
    title: "Verified Supplier Network",
    description: "Every supplier in our network undergoes rigorous verification, ensuring you work only with trustworthy, quality-certified manufacturers.",
  },
  {
    icon: Globe,
    title: "Global Trade Expertise",
    description: "Deep understanding of international trade regulations, customs procedures, and documentation requirements across 100+ countries.",
  },
  {
    icon: Clock,
    title: "Efficient Turnaround",
    description: "Streamlined processes and established relationships enable faster sourcing, production, and delivery timelines.",
  },
  {
    icon: HeartHandshake,
    title: "Dedicated Partnership",
    description: "More than a vendor, we're your strategic partner — invested in your success and committed to long-term relationships.",
  },
  {
    icon: TrendingUp,
    title: "Competitive Pricing",
    description: "Direct manufacturer relationships and bulk procurement expertise ensure you get the best prices without compromising quality.",
  },
  {
    icon: BadgeCheck,
    title: "Quality Assurance",
    description: "Multi-stage quality control processes and pre-shipment inspections guarantee products meet your exact specifications.",
  },
];

const WhyViatra = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              Why Choose <span className="text-gradient-gold">VIATRA</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              In a world of countless export options, VIATRA stands apart through unwavering 
              commitment to quality, transparency, and partner success. Here's what makes us 
              the preferred choice for global B2B trade.
            </p>
          </div>
        </div>
      </section>

      {/* Reasons Grid */}
      <section className="py-16 bg-card/50">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reasons.map((reason) => (
              <div
                key={reason.title}
                className="p-8 rounded-2xl bg-gradient-card border border-border hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center mb-6">
                  <reason.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-foreground mb-3">
                  {reason.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: "500+", label: "Verified Suppliers" },
              { value: "100+", label: "Countries Served" },
              { value: "10K+", label: "Shipments Completed" },
              { value: "98%", label: "Client Satisfaction" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl md:text-5xl font-serif font-bold text-gradient-gold mb-2">
                  {stat.value}
                </div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-card/50">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
              Experience the <span className="text-gradient-gold">VIATRA</span> Difference
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your journey with a partner who truly understands international trade.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button variant="gold" size="xl">
                  Partner With Us
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="goldOutline" size="lg">
                  Explore Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WhyViatra;
